import { epochSecondsToDate, z } from '@customerio/hydra-validation';
import type { Tag as UIAPITag } from '@customerio/hydra-ui-api';
import { defineEntity, readSchema, writeSchema } from '../shared/define-entity';
import { idAndTimestampsToApi } from '../shared/dates';

/**
 * Tag — sideload-only entity in this pilot. Rows arrive populated via
 * `getCampaigns` cluster propagation; a real fetch-capable
 * `getTagQueryOptions` exists in `tag/queries.ts` for the rare detail
 * miss (e.g. workspace clear evicts a tag before its parent campaign).
 *
 * **`id` is `string` in the entity.** The wire encodes it as a number
 * (`UIAPITag.id: number`) but every other entity in this layer
 * uses string ids and `Campaign.tag_ids` is `string[]` on the wire too,
 * so we coerce at the parse boundary (number → string) and back at the
 * serialize boundary (string → number) to keep one consistent type
 * across consumers and FK-follow paths.
 *
 * The `'campaigns'` relation is the inverse of campaign↔tags: campaigns
 * that reference this tag id. Read via `useCampaignsCached` + `select` (see
 * `useCampaignsForTag`) — best-effort over what the cache has seen, not
 * an authoritative server view.
 */

export type Tag = Omit<UIAPITag, 'id' | 'created' | 'updated'> & {
  id: string;
  created: Date;
  updated: Date;
};

type TagWriteInput = Partial<Tag>;
type TagWireWritePayload = Partial<UIAPITag>;

const TagFromApi = readSchema<Tag>(
  z.looseObject({
    id: z.number().transform(String),
    created: epochSecondsToDate,
    updated: epochSecondsToDate,
  }),
);

const TagToApi = writeSchema<TagWriteInput, TagWireWritePayload>(z.looseObject(idAndTimestampsToApi));

type TagRelations = {
  readonly campaigns: {
    readonly kind: 'has-many';
    readonly target: 'campaign';
    readonly via: { readonly on: 'target'; readonly field: 'tag_ids' };
  };
};

/** @public */
export const tag = defineEntity<Tag, TagWireWritePayload, void, TagRelations, 'workspace'>({
  name: 'tag',
  scope: 'workspace',
  pk: (t) => t.id,
  schema: { read: TagFromApi, write: TagToApi },
  relations: {
    campaigns: { kind: 'has-many', target: 'campaign', via: { on: 'target', field: 'tag_ids' } },
  },
});

export const tagKeys = tag.keys;

export function parseTag(raw: UIAPITag): Tag {
  return tag.parse(raw);
}
